import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EZIOKResult } from '../libs/EZIOKService';

/**
 * EZ-IOK 간편인증 콜백 페이지
 *
 * 라우터에 등록 필요:
 * <Route path="/auth/eziok/callback" element={<EZIOKCallback />} />
 *
 * 백엔드에서 인증 결과를 쿼리 파라미터로 전달하면
 * 부모 창으로 postMessage 전송 후 자동 닫힘
 */
function EZIOKCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [result, setResult] = useState<EZIOKResult | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // 백엔드에서 전달한 쿼리 파라미터 파싱
    const success = searchParams.get('success') === 'true';
    const message = searchParams.get('message') || '';
    const name = searchParams.get('name') || undefined;
    const ci = searchParams.get('ci') || undefined;
    const di = searchParams.get('di') || undefined;
    const birthDate = searchParams.get('birthDate') || undefined;
    const gender = searchParams.get('gender') || undefined;
    const nationalInfo = searchParams.get('nationalInfo') || undefined;
    const mobileNo = searchParams.get('mobileNo') || undefined;

    const eziokResult: EZIOKResult = {
      success,
      message,
      data: { name, ci, di, birthDate, gender, nationalInfo, mobileNo },
    };

    if (success) {
      setResult(eziokResult);
      // 부모 창으로 성공 메시지 전송
      if (window.opener) {
        window.opener.postMessage(eziokResult, '*');
        setTimeout(() => window.close(), 1000);
      } else {
        sessionStorage.setItem('eziok_result', JSON.stringify(eziokResult));
        navigate(-1);
      }
    } else {
      const err = new Error(message || '인증에 실패했습니다.');
      setError(err);
      // 부모 창으로 에러 메시지 전송
      if (window.opener) {
        window.opener.postMessage(eziokResult, '*');
        setTimeout(() => window.close(), 2000);
      } else {
        sessionStorage.setItem('eziok_error', JSON.stringify({ message: err.message }));
        navigate(-1);
      }
    }

    setIsLoading(false);
  }, [searchParams, navigate]);

  // 부모 창 존재 여부 로깅
  useEffect(() => {
    if (!window.opener) {
      console.log(
        '[EZIOKCallback] No opener window. Using session storage for result.',
      );
    }
  }, []);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <p style={{ color: '#666', fontSize: '14px' }}>
          간편인증 결과를 처리하고 있습니다...
        </p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <p style={{ color: '#e74c3c', fontSize: '16px', fontWeight: 'bold' }}>
          간편인증에 실패했습니다
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>{error.message}</p>
        <button
          onClick={() => window.close()}
          style={{
            padding: '12px 24px',
            backgroundColor: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          닫기
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <p style={{ color: '#27ae60', fontSize: '16px', fontWeight: 'bold' }}>
          간편인증이 완료되었습니다
        </p>
        <p style={{ color: '#666', fontSize: '14px' }}>
          잠시 후 자동으로 닫힙니다...
        </p>
      </div>
    );
  }

  return null;
}

export default EZIOKCallback;